/**
 * `/login`.
 *
 * There is no design handoff for this screen — `docs/design/Lead Manager.dc.html`
 * starts at the lead board, already signed in — so it is a plain centred shadcn
 * Card (email, password, submit) carrying the brand palette and logo like the
 * rest of the app.
 *
 * Three copy rules here are security or field-usability decisions, not
 * wording preferences. Each is commented where it applies:
 *   1. Wrong email and wrong password produce the SAME message.
 *   2. Rate limiting gets its own message that says to wait.
 *   3. Being offline gets its own message that says so, instead of being
 *      reported as bad credentials.
 *
 * There is deliberately no sign-up link and no forgot-password link. Both
 * routes are disabled server-side (accounts are created by the office), so a
 * link would be a dead end that a salesperson taps at a booth and then waits
 * on.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import logoUrl from '@/assets/logo-h.png';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useSessionStore, waitForSessionBoot, type SignInOutcome } from '@/lib/auth/session';

const LoginSearch = z.object({
  /** Where the guard bounced the user from, so sign-in can send them back. */
  redirect: z.string().optional()
});

const LoginForm = z.object({
  email: z.email('Enter a valid email address.'),
  password: z.string().min(1, 'Enter your password.')
});
type LoginForm = z.infer<typeof LoginForm>;

export const Route = createFileRoute('/login')({
  validateSearch: LoginSearch,
  beforeLoad: async () => {
    await waitForSessionBoot();
    // Already signed in (including from the IndexedDB mirror, with no network
    // at all) — never show a login screen to someone who does not need it.
    //
    // Runs once, at route load, and the router never re-runs it on a store
    // change — LoginScreen's effect covers the later flip.
    if (useSessionStore.getState().status === 'authenticated') {
      throw redirect({ to: '/' });
    }
  },
  component: LoginScreen
});

/**
 * The single place sign-in failures become words.
 *
 * `invalid-credentials` covers both a nonexistent email and a wrong password
 * because Better Auth returns one indistinguishable 401 for both (it even
 * hashes the supplied password for an unknown user to keep the timing
 * similar). This app is reachable from the public internet, so an endpoint
 * that told the two apart would be an account-enumeration oracle: type an
 * address, learn whether it belongs to a real employee. Do not "improve" this
 * into two messages.
 */
function messageFor(outcome: Exclude<SignInOutcome, { kind: 'success' }>): string {
  switch (outcome.kind) {
    case 'invalid-credentials':
      return 'Email or password is incorrect.';
    case 'rate-limited':
      // Better Auth limits /sign-in/email to 5 attempts a minute. Without this
      // branch a rate-limited 429 would render as "incorrect password", and
      // someone who mistyped twice would keep retyping a password that was
      // right the whole time.
      return 'Too many sign-in attempts. Wait about a minute, then try again.';
    case 'offline':
      // The distinction that matters most at a booth. Telling someone their
      // password is wrong when the real problem is signal is a bad half-hour.
      return 'Cannot reach the server. Signing in needs a connection — check wifi or mobile data, then try again.';
    case 'server-error':
      return outcome.message;
  }
}

/**
 * Resolve a post-login `redirect` to a safe *internal* location, or `null`.
 *
 * `redirect` arrives in the URL, so a crafted link
 * (`/login?redirect=//evil.com`) must not be able to bounce a
 * just-authenticated user off-site — an open redirect, and a convincing
 * phishing primitive precisely because the hop starts on the real, trusted
 * origin. A leading `/` is not proof of same-origin: `//host` and `/\host`
 * are both protocol-relative and land on `host`. Resolve against our own
 * origin, require it to stay there, and hand back the path only — never a
 * full URL.
 */
function sameOriginPath(redirect: string): string | null {
  try {
    const url = new URL(redirect, window.location.origin);
    if (url.origin !== window.location.origin) return null;
    return url.pathname + url.search + url.hash;
  } catch {
    return null;
  }
}

function LoginScreen() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const signIn = useSessionStore(state => state.signIn);
  const connectivity = useSessionStore(state => state.connectivity);
  const status = useSessionStore(state => state.status);
  const [formError, setFormError] = useState<string | null>(null);

  /**
   * `search.redirect` is a fully built href from the router, so it goes through
   * `href:` rather than the type-checked `to:` (which would need a cast). It
   * rides in the URL and is therefore attacker-controllable — hence the
   * same-origin check.
   */
  const goToTarget = useCallback(async () => {
    if (search.redirect !== undefined) {
      const safePath = sameOriginPath(search.redirect);
      if (safePath !== null) {
        await navigate({ href: safePath });
        return;
      }
    }
    await navigate({ to: '/' });
  }, [navigate, search.redirect]);

  /**
   * Leave as soon as the session becomes authenticated, whoever made it so.
   *
   * boot() reads the session from Dexie only and fires revalidate() unawaited,
   * so a device holding a valid cookie with no cached Dexie row (private mode,
   * Android reclaiming storage) lands here and flips a moment later. Without
   * this it sat on /login with a server-verified session, showing a password
   * form to someone who may not have one — accounts are created by the office.
   */
  useEffect(() => {
    if (status !== 'authenticated') return;
    void goToTarget();
  }, [status, goToTarget]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<LoginForm>({
    resolver: zodResolver(LoginForm),
    defaultValues: { email: '', password: '' }
  });

  const onSubmit = handleSubmit(async values => {
    setFormError(null);
    const outcome = await signIn(values);
    if (outcome.kind !== 'success') {
      setFormError(messageFor(outcome));
      return;
    }
    // The effect above will also fire once the store flips, and both land on
    // the same place — but navigating here keeps the successful-sign-in path
    // independent of store timing rather than newly dependent on it.
    await goToTarget();
  });

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center p-6">
      <div className="flex w-full max-w-sm flex-col gap-6">
        <header className="flex flex-col items-center gap-3">
          <img src={logoUrl} alt="The Paradise" className="h-10 w-auto" />
          <h1 className="text-muted-foreground text-sm font-medium tracking-wide uppercase">Lead Manager</h1>
        </header>

        {/* A device that has never signed in cannot sign in without
            connectivity — the session is database-backed, so there is nothing
            local to check a password against. Say so up front rather than
            letting someone type a correct password into a dead form. */}
        {connectivity === 'offline' && (
          <Card role="status">
            <CardContent className="text-sm">
              <span className="font-semibold">No connection.</span> Signing in needs the server. If you have signed in
              on this device before, close and reopen the app — it will pick the session back up without a connection.
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent>
            <form onSubmit={e => void onSubmit(e)} className="flex flex-col gap-4" noValidate>
              <div className="flex flex-col gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  inputMode="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  aria-invalid={errors.email ? true : undefined}
                  aria-describedby={errors.email ? 'email-error' : undefined}
                  {...register('email')}
                />
                {errors.email && (
                  <p id="email-error" className="text-sm text-destructive">
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  aria-invalid={errors.password ? true : undefined}
                  aria-describedby={errors.password ? 'password-error' : undefined}
                  {...register('password')}
                />
                {errors.password && (
                  <p id="password-error" className="text-sm text-destructive">
                    {errors.password.message}
                  </p>
                )}
              </div>

              {/* aria-live so a screen reader announces a failure that appears
                  after submit without the focus having moved. */}
              {formError && (
                <p role="alert" aria-live="polite" className="text-sm text-destructive">
                  {formError}
                </p>
              )}

              <Button type="submit" disabled={isSubmitting} className="mt-2 w-full">
                {isSubmitting ? 'Signing in…' : 'Sign in'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* No sign-up and no password-reset link: both are disabled on the
            server, so either would be a dead end. Accounts come from the
            office. */}
        <p className="text-muted-foreground text-xs">
          Accounts are created by the office. Ask them if you cannot get in.
        </p>
      </div>
    </main>
  );
}
